import type {ReactNode} from 'react'
import styles from './ReferenceChipList.module.css'

export interface ReferenceChipListProps {
  /**
   * Accessible label announced for the list (e.g. "Attachments"). Omit when an
   * ancestor already labels the surrounding context.
   */
  ariaLabel?: string
  /**
   * The reference items to lay out (e.g. `ReferenceChip`s). Each item is
   * wrapped in its own list item.
   */
  items: ReactNode[]
}

/**
 * Pure layout wrapper that arranges reference items (e.g. `ReferenceChip`) in
 * a wrapping row and exposes them as an accessible list. Handles spacing and
 * list semantics only; item content and interaction are the caller's
 * responsibility.
 */
export function ReferenceChipList({ariaLabel, items}: ReferenceChipListProps) {
  return (
    // Safari/VoiceOver doesn't announce `<ul>` elements as lists when
    // `list-style: none` is set in CSS (WCAG 1.3.1). Adding `role="list"`
    // restores the list semantics so AT users hear "list, N items".
    // eslint-disable-next-line jsx-a11y/no-redundant-roles
    <ul className={styles.list} aria-label={ariaLabel} role="list">
      {items.map((item, index) => (
        // eslint-disable-next-line @eslint-react/no-array-index-key -- items are a static, order-stable list with no stable id
        <li key={index} className={styles.item}>
          {item}
        </li>
      ))}
    </ul>
  )
}
